import { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/roles";
import type { Project } from "@/types/project";
import { ProjectAssignments } from "./ProjectAssignments";
import { SponsorPicker } from "@/components/ui/SponsorPicker";
import { useRoster } from "@/hooks/useRoster";
import { usePortfolio } from "@/hooks/usePortfolio";
import { useDepartmentNames } from "@/hooks/useDepartmentNames";
import { useDepartments } from "@/hooks/useDepartments";
import { useAppSettings } from "@/hooks/useAppSettings";
import { costRangeLabel, readBlendedRate } from "@/lib/cost";
import {
  useProjectRoleConfig,
  useUpdateProjectRoleConfig,
  type RoleConfigRow,
} from "@/hooks/useRoleConfig";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCreateProject, useUpdateProject, useNextProjectId } from "@/hooks/usePortfolio";

import { STATUS_OPTIONS, HEALTH_OPTIONS as HEALTH_META } from "@/lib/projectStatus";

const PRIORITY_OPTIONS = ["Highest", "High", "Medium", "Low"];

const PHASE_OPTIONS = [
  { value: "", label: "— not set —" },
  { value: "planning", label: "Planning" },
  { value: "execution", label: "Execution" },
  { value: "testing_go_live", label: "Testing / Go Live" },
];

const ROLE_KEYS = [
  { key: "pm", label: "PM" },
  { key: "ba", label: "BA" },
  { key: "functional", label: "Functional" },
  { key: "technical", label: "Technical" },
  { key: "developer", label: "Developer" },
  { key: "infrastructure", label: "Infra" },
  { key: "dba", label: "DBA" },
  { key: "erp", label: "ERP" },
  { key: "operations", label: "Operations" },
];

interface Props {
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Start the new project in this department (create mode only) — the
   *  department page's Add project button. */
  presetDepartment?: string | null;
}

type FormState = {
  id: string;
  name: string;
  status: string;
  health: string;          // "" | "green" | "yellow" | "red"
  priority: string;
  pct_complete: string;
  est_hours: string;
  start_date: string;
  end_date: string;
  current_phase: string;
  team: string;
  // Sponsor (SponsorPicker) and department (dropdown of existing
  // department strings).
  sponsor: string;
  department: string;
  /** Parent project id when this row is a child. Empty = top-level. */
  parent_project_id: string;
  functional_spec_due: string;
  functional_spec_completed: string;
  technical_spec_due: string;
  technical_spec_completed: string;
  description: string;
  // T-shirt size — coarse effort bucket. Stored as the full label
  // ("M: 80-160 Hours") so the bucket meaning is preserved without
  // requiring a separate lookup table.
  tshirt_size: string;
  role_allocations: Record<string, string>;
};

// Canonical t-shirt sizes seen in the project corpus. Stored verbatim so
// the bucket boundaries (in hours) live with the value. Edit here if
// the team revises the buckets — UI stays in sync without a code change
// elsewhere.
const TSHIRT_OPTIONS = [
  "S: < 80 Hours",
  "M: 80-160 Hours",
  "L: 160-320 Hours",
  "XL: 320-640 Hours",
  "XXL: > 640 Hours",
];

function blank(): FormState {
  return {
    id: "",
    name: "",
    status: "approved",
    health: "",
    priority: "Medium",
    pct_complete: "0",
    est_hours: "0",
    start_date: "",
    end_date: "",
    current_phase: "",
    team: "",
    sponsor: "",
    department: "",
    parent_project_id: "",
    functional_spec_due: "",
    functional_spec_completed: "",
    technical_spec_due: "",
    technical_spec_completed: "",
    description: "",
    tshirt_size: "",
    role_allocations: Object.fromEntries(ROLE_KEYS.map((r) => [r.key, "0"])),
  };
}

function fromProject(p: Project): FormState {
  return {
    id: p.id,
    name: p.name,
    status: p.status ?? "approved",
    health: p.health ?? "",
    priority: p.priority ?? "Medium",
    pct_complete: Math.round(p.pct_complete * 100).toString(),
    est_hours: (p.est_hours || 0).toString(),
    start_date: p.start_date ?? "",
    end_date: p.end_date ?? "",
    current_phase: p.current_phase ?? "",
    team: (p as any).team ?? "",
    sponsor: p.sponsor ?? "",
    department: p.department ?? "",
    parent_project_id: p.parent_project_id ?? "",
    functional_spec_due: p.functional_spec_due ?? "",
    functional_spec_completed: p.functional_spec_completed ?? "",
    technical_spec_due: p.technical_spec_due ?? "",
    technical_spec_completed: p.technical_spec_completed ?? "",
    description: p.description ?? "",
    tshirt_size: p.tshirt_size ?? "",
    role_allocations: Object.fromEntries(
      ROLE_KEYS.map((r) => [
        r.key,
        Math.round((p.role_allocations?.[r.key] ?? 0) * 100).toString(),
      ]),
    ),
  };
}

export function EditProjectDialog({ project, open, onOpenChange, presetDepartment }: Props) {
  const isEdit = project !== null;
  // Go-live change note (2026-09-28, option 1): the same optional
  // "why, and who approved" the Details card asks for, offered here too
  // so a move made through this form isn't silently unexplained in the
  // Monday email. Only when an EXISTING go-live changes.
  const [changeReason, setChangeReason] = useState("");
  const [changeApprovedBy, setChangeApprovedBy] = useState("");
  const { isPmo } = useCan();
  const [form, setForm] = useState<FormState>(() =>
    project ? fromProject(project) : blank(),
  );
  // Users can't create straight to a stage — the server forces Requested —
  // unless the department is self-managed (2026-10-22, IT Infrastructure):
  // then the project is saved Approved (or the stage they pick), off the board.
  const { data: departmentRows } = useDepartments();
  const selfManagedDept = !!departmentRows?.find((d) => d.name.toLowerCase() === form.department.trim().toLowerCase())?.self_managed;
  const lockedToRequested = !isEdit && !isPmo && !selfManagedDept;
  const goLiveMoved = isEdit && !!project?.end_date && (form.end_date || "") !== (project?.end_date ?? "");
  // Department picker mode — flips to a free-text input when the user
  // picks "+ Add new…" from the select so they can type a department
  // that doesn't exist yet. Reset on dialog open.
  const [addingDepartment, setAddingDepartment] = useState(false);
  const { data: rosterData } = useRoster();
  const teamList = useMemo(() => {
    const roster = Array.isArray(rosterData) ? rosterData : [];
    const teams = new Set(roster.map((m) => m.team).filter(Boolean) as string[]);
    return Array.from(teams).sort();
  }, [rosterData]);

  // Cost setting — pulled fresh so the displayed cost range reflects
  // whatever rate Settings has set right now. No per-project cache.
  const { data: costSettings } = useAppSettings("cost");
  const blendedRate = readBlendedRate(costSettings);

  // Department dropdown options derived from existing projects'
  // department values — keeps the user picking from a known set without
  // forcing a hardcoded enum. Free-text via `<input list>` so they can
  // also add a new department name on the fly.
  // One list everywhere (2026-10-22): the Admin departments list plus any
  // value still on a project.
  const { data: allProjects } = usePortfolio();
  const departmentList = useDepartmentNames();

  // Candidate parent projects — the parent picker shows only TOP-LEVEL
  // rows (parent_project_id null/empty) so we never let the user
  // create a 2-level tree via the UI. Also excludes:
  //   - the project being edited (no self-reference)
  //   - any project that already has children — it could only be made
  //     a child if those children were detached first; not worth
  //     wedging the UX for that edge case
  // Sorted by id for predictable scanning.
  const parentCandidates = useMemo(() => {
    const editingId = form.id;
    return (allProjects ?? [])
      .filter(
        (p) =>
          !p.parent_project_id &&
          p.id !== editingId &&
          (p.child_count ?? 0) === 0,
      )
      .slice()
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [allProjects, form.id]);
  // Show the picker disabled with an explanation if THIS project has
  // children — making a parent into a child would break the one-level
  // depth invariant.
  const hasOwnChildren = (() => {
    const me = (allProjects ?? []).find((p) => p.id === form.id);
    return (me?.child_count ?? 0) > 0;
  })();

  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject();
  const mutation = isEdit ? updateMutation : createMutation;
  const { data: nextIdData } = useNextProjectId();

  // The next-ID lookup only matters when CREATING. In edit mode it must
  // not re-run this effect: it resolves after the dialog opens, and the
  // reset silently wiped anything typed in the meantime (2026-09-29 —
  // caught by the go-live-note e2e, where the new date vanished before
  // Save and the change never journaled).
  const nextIdDep = project ? null : nextIdData?.next_id;
  useEffect(() => {
    if (open) {
      if (project) {
        setForm(fromProject(project));
      } else {
        const b = blank();
        if (nextIdData?.next_id) b.id = nextIdData.next_id;
        if (presetDepartment) b.department = presetDepartment;
        setForm(b);
      }
      createMutation.reset();
      updateMutation.reset();
      setAddingDepartment(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id, nextIdDep]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setAlloc = (roleKey: string, value: string) =>
    setForm((f) => ({
      ...f,
      role_allocations: { ...f.role_allocations, [roleKey]: value },
    }));

  const canSubmit = form.id.trim() !== "" && form.name.trim() !== "";

  const handleSave = async () => {
    if (!canSubmit) return;

    const roleAllocations: Record<string, number> = {};
    for (const [k, v] of Object.entries(form.role_allocations)) {
      const pct = parseFloat(v) || 0;
      roleAllocations[k] = Math.max(0, Math.min(1, pct / 100));
    }

    const payload: Record<string, unknown> = {
      id: form.id.trim(),
      name: form.name.trim(),
      status: lockedToRequested ? "requested" : form.status || null,
      health: form.health || null,
      priority: form.priority || null,
      pct_complete: Math.max(0, Math.min(1, (parseFloat(form.pct_complete) || 0) / 100)),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      ...(goLiveMoved && changeReason.trim() ? { change_reason: changeReason.trim() } : {}),
      ...(goLiveMoved && changeApprovedBy.trim() ? { change_approved_by: changeApprovedBy.trim() } : {}),
      est_hours: parseFloat(form.est_hours) || 0,
      current_phase: form.current_phase || null,
      team: form.team || null,
      sponsor: form.sponsor.trim() || null,
      department: form.department.trim() || null,
      parent_project_id: form.parent_project_id || null,
      functional_spec_due: form.functional_spec_due || null,
      functional_spec_completed: form.functional_spec_completed || null,
      technical_spec_due: form.technical_spec_due || null,
      technical_spec_completed: form.technical_spec_completed || null,
      description: form.description.trim() || null,
      tshirt_size: form.tshirt_size || null,
      role_allocations: roleAllocations,
    };

    // Detect whether a completed date is being newly set on this save,
    // so we can show a toast if the backend auto-transitions status.
    const funcJustDone =
      isEdit &&
      !project?.functional_spec_completed &&
      !!form.functional_spec_completed;
    const techJustDone =
      isEdit &&
      !project?.technical_spec_completed &&
      !!form.technical_spec_completed;
    const priorStatus = project?.status ?? null;

    try {
      let result: Project | undefined;
      if (isEdit) {
        result = await updateMutation.mutateAsync(
          payload as Record<string, unknown> & { id: string },
        );
      } else {
        result = (await createMutation.mutateAsync(payload)) as Project | undefined;
      }

      // If the backend auto-transitioned status (response differs from submission), notify.
      if (isEdit && result && result.status !== form.status) {
        const statusReadable = result.status?.replace(/_/g, " ") ?? "";
        if (techJustDone) {
          toast.success("Technical spec marked complete", {
            description: `${result.name} moved to ${statusReadable}.`,
          });
        } else if (funcJustDone) {
          toast.success("Functional spec marked complete", {
            description: `${result.name} moved to ${statusReadable}.`,
          });
        } else if (priorStatus !== result.status) {
          toast.success("Project updated", {
            description: `Status: ${statusReadable}`,
          });
        }
      }

      onOpenChange(false);
    } catch {
      /* error surfaces via mutation.error */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit project" : "Add project"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update sizing, dates, and role allocations."
              : "Add a new project to the portfolio."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {/* ID + Name */}
          <div className="grid grid-cols-[100px_1fr] gap-3">
            <Field label="ID">
              <input
                type="text"
                value={form.id}
                onChange={(e) => set("id", e.target.value)}
                placeholder="ETE-150"
                disabled={isEdit}
                className={inputCls + (isEdit ? " bg-canvas text-ink-muted" : "")}
              />
            </Field>
            <Field label="Name">
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Project name"
                autoFocus={!isEdit}
                className={inputCls}
              />
            </Field>
          </div>

          {/* Description — one-line elevator pitch shown in the Overview
              hero. Distinct from the Brief's business_need (formal,
              paragraph-length intake artifact) — this is the 2-second
              orientation for anyone landing on the project cold. */}
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="One line: what this project does and why it matters."
              rows={2}
              maxLength={500}
              className={cn(inputCls, "resize-none")}
            />
            {/* Unobtrusive counter — slate at default, amber near cap,
                rose at cap. Matches the inline editor on Project
                Detail's Overview hero. */}
            <div className={cn(
              "mt-0.5 text-right text-[10px] tabular-nums",
              form.description.length >= 500 ? "text-rose-500"
                : form.description.length >= 450 ? "text-amber-600 dark:text-amber-400"
                : "text-ink-subtle",
            )}>
              {form.description.length} / 500
            </div>
          </Field>

          {/* Status, Health, Priority */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Status">
              {/* A User's new project always lands on Intake as Requested
                  (2026-09-28); only the PMO can create straight to a stage. */}
              {lockedToRequested && (
                <p className="mb-1 text-[11px] text-amber-700 dark:text-amber-300" data-testid="create-goes-to-intake">Goes to Intake for the PMO to approve.</p>
              )}
              {!isEdit && !isPmo && selfManagedDept && (
                <p className="mb-1 text-[11px] text-emerald-700 dark:text-emerald-300" data-testid="create-self-managed">{form.department} manages its own projects — no PMO approval needed.</p>
              )}
              <select value={lockedToRequested ? "requested" : form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} disabled={lockedToRequested} data-testid="project-status-select">
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Health">
              <select
                value={form.health}
                onChange={(e) => set("health", e.target.value)}
                className={inputCls}
                title="Risk indicator. Pick N/A for projects you don't want to assess yet."
              >
                <option value="">—</option>
                {HEALTH_META.map((h) => (
                  <option key={h.key} value={h.key}>{h.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Priority">
              <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className={inputCls}>
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Hours, % Complete, T-Shirt, Phase. T-shirt is the coarse
              effort bucket that drives early planning before est_hours
              gets refined; both fields coexist on purpose. */}
          <div className="grid grid-cols-4 gap-3">
            <Field label="Est. Hours">
              <input
                type="number"
                min={0}
                step={10}
                value={form.est_hours}
                onChange={(e) => set("est_hours", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Progress">
              <input
                type="number"
                min={0}
                max={100}
                step={5}
                value={form.pct_complete}
                onChange={(e) => set("pct_complete", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="T-Shirt Size">
              <select value={form.tshirt_size} onChange={(e) => set("tshirt_size", e.target.value)} className={inputCls}>
                <option value="">— none —</option>
                {TSHIRT_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {/* Computed cost range. Pulled from app_settings.cost_blended_rate
                  on every open, so an exec who changes the rate in Settings
                  sees the new range here next time they open this dialog. */}
              <div className="mt-1 text-[10px] text-ink-muted">
                Est. Cost: <span className="font-semibold tabular-nums text-ink-2">{costRangeLabel(form.tshirt_size, blendedRate)}</span>
                <span className="ml-1 text-ink-subtle">@ ${blendedRate}/hr</span>
              </div>
            </Field>
            <Field label="Current Phase">
              <select value={form.current_phase} onChange={(e) => set("current_phase", e.target.value)} className={inputCls}>
                {PHASE_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Dates + Team */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Start Date">
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="End Date">
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => set("end_date", e.target.value)}
                className={inputCls}
              />
            </Field>
            {goLiveMoved && (
              <div className="col-span-full rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/10 p-3" data-testid="dialog-date-change-note">
                <p className="mb-2 text-xs text-amber-800 dark:text-amber-300">
                  Go-live moving {project?.end_date} → {form.end_date || "unset"}. Why, and who approved? Shows in the Monday board email. Optional.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    placeholder="Reason (one line)"
                    aria-label="Reason for the go-live change"
                    className={inputCls}
                  />
                  <input
                    value={changeApprovedBy}
                    onChange={(e) => setChangeApprovedBy(e.target.value)}
                    placeholder="Approved by"
                    aria-label="Who approved the go-live change"
                    className={inputCls}
                  />
                </div>
              </div>
            )}
            <Field label="Team">
              <select value={form.team} onChange={(e) => set("team", e.target.value)} className={inputCls}>
                <option value="">— none —</option>
                {teamList.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            {/* Sponsor — SponsorPicker. Source is every sponsor name
                in use across projects, so a typo here doesn't silently mint a duplicate "Ray Trumbel
                vs. Ray Trumble" entry that fragments the filter
                dropdowns elsewhere. "+ Add new…" introduces a brand-new
                sponsor inline when an actual new person is being
                tagged. */}
            <Field label="Sponsor">
              <SponsorPicker
                value={form.sponsor || null}
                onChange={(v) => set("sponsor", v ?? "")}
                autoFocus={false}
                className={inputCls + " w-full"}
              />
            </Field>
            {/* Department — real <select> over the known department
                list, plus an "+ Add new…" option that flips the field
                to a text input for typing a brand-new department.
                Avoids the browser-autocomplete pollution the previous
                <input list> combo suffered from while still allowing
                new department names to be introduced inline. */}
            <Field label="Department">
              {addingDepartment ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    autoFocus
                    value={form.department}
                    onChange={(e) => set("department", e.target.value)}
                    placeholder="New department name"
                    className={inputCls}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setAddingDepartment(false);
                      set("department", "");
                    }}
                    className="text-xs text-ink-muted hover:text-ink-2"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <select data-testid="project-department-select"
                  value={form.department}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__add_new__") {
                      setAddingDepartment(true);
                      set("department", "");
                    } else {
                      set("department", v);
                    }
                  }}
                  className={inputCls}
                >
                  <option value="">— none —</option>
                  {departmentList.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                  {form.department &&
                    !departmentList.includes(form.department) && (
                      <option value={form.department}>{form.department}</option>
                    )}
                  <option value="__add_new__">+ Add new…</option>
                </select>
              )}
            </Field>
            {/* Parent project — links this row as a CHILD of a larger
                program. Disabled when the project already has its own
                children (can't be a parent AND a child). */}
            <Field label="Parent project">
              <select
                value={form.parent_project_id}
                onChange={(e) => set("parent_project_id", e.target.value)}
                disabled={hasOwnChildren}
                className={cn(
                  inputCls,
                  hasOwnChildren && "bg-canvas text-ink-subtle",
                )}
                title={
                  hasOwnChildren
                    ? "This project has children; it cannot itself be a child."
                    : "Pick a parent program. Leave blank for top-level."
                }
              >
                <option value="">— none (top-level) —</option>
                {parentCandidates.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.id} · {p.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Spec Tracking */}
          <div className="rounded-lg border border-line bg-canvas/60 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
                Spec Tracking
              </div>
              <div className="text-[10px] text-ink-subtle">
                BA owns. Setting a completed date moves the project forward.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Functional Spec Due">
                <input
                  type="date"
                  value={form.functional_spec_due}
                  onChange={(e) => set("functional_spec_due", e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Functional Spec Completed">
                <input
                  type="date"
                  value={form.functional_spec_completed}
                  onChange={(e) => set("functional_spec_completed", e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Technical Spec Due">
                <input
                  type="date"
                  value={form.technical_spec_due}
                  onChange={(e) => set("technical_spec_due", e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Technical Spec Completed">
                <input
                  type="date"
                  value={form.technical_spec_completed}
                  onChange={(e) => set("technical_spec_completed", e.target.value)}
                  className={inputCls}
                />
              </Field>
            </div>
          </div>

          {/* Role Configuration — allocation % + phase splits */}
          {isEdit && (
            <ProjectRoleConfigSection projectId={form.id} />
          )}
          {!isEdit && (
            <div className="rounded-lg border border-line bg-canvas/60 p-3">
              <div className="text-xs text-ink-muted">
                Role configuration will be available after creating the project. New projects use global defaults from Settings.
              </div>
            </div>
          )}

          {/* Team Assignments — only show in edit mode */}
          {isEdit && (
            <ProjectAssignments projectId={form.id} />
          )}

          {mutation.isError && (
            <div className="rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-800 dark:text-red-300">
              Failed: {(mutation.error as Error).message}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={mutation.isPending || !canSubmit}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Save" : "Add project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Inline Role Config Section (per-project overrides)
// ---------------------------------------------------------------------------

const ROLE_ORDER = ["pm", "ba", "functional", "technical", "developer", "infrastructure", "dba", "erp"];
const ROLE_LABEL_MAP: Record<string, string> = {
  pm: "PM", ba: "BA", functional: "Functional", technical: "Technical",
  developer: "Developer", infrastructure: "Infra", dba: "DBA", erp: "ERP",
};

function ProjectRoleConfigSection({ projectId }: { projectId: string }) {
  const { data: config, isLoading } = useProjectRoleConfig(projectId);
  const mutation = useUpdateProjectRoleConfig(projectId);
  const [draft, setDraft] = useState<RoleConfigRow[] | null>(null);

  // Sync draft from server data
  useEffect(() => {
    if (config?.roles) {
      setDraft(config.roles.map((r) => ({ ...r })));
    }
  }, [config?.roles]);

  // Active roles = the ones the user has selected for this project (alloc > 0).
  // Unused roles stay in the draft as 0% rows so the data shape stays
  // consistent with the backend; they're just not rendered until the user
  // adds them via the "Add a role" picker.
  const activeRoles = useMemo(() => {
    if (!draft) return [];
    return draft
      .filter((r) => (r.allocation_pct ?? 0) > 0)
      .sort((a, b) => (ROLE_ORDER.indexOf(a.role_key) ?? 99) - (ROLE_ORDER.indexOf(b.role_key) ?? 99));
  }, [draft]);

  const unusedRoles = useMemo(() => {
    if (!draft) return [];
    return draft
      .filter((r) => (r.allocation_pct ?? 0) <= 0)
      .sort((a, b) => (ROLE_ORDER.indexOf(a.role_key) ?? 99) - (ROLE_ORDER.indexOf(b.role_key) ?? 99));
  }, [draft]);

  const handleChange = (roleKey: string, field: keyof RoleConfigRow, value: number) => {
    setDraft((prev) =>
      prev?.map((r) => (r.role_key === roleKey ? { ...r, [field]: value } : r)) ?? null,
    );
  };

  // Adding a role = bumping its allocation from 0 to a sensible default
  // (10%). Removing = setting back to 0; the row drops out of activeRoles
  // on the next render but the underlying record stays so phase splits
  // are preserved if the user re-adds it.
  const addRole = (roleKey: string) => handleChange(roleKey, "allocation_pct", 0.10);
  const removeRole = (roleKey: string) => handleChange(roleKey, "allocation_pct", 0);

  const handleSave = () => {
    if (!draft) return;
    // Only validate phase splits on roles the user has actually selected.
    // Unused roles (alloc 0) keep whatever phase splits exist on them
    // without forcing the user to clean them up — they're not generating
    // demand anyway.
    for (const role of draft) {
      if ((role.allocation_pct ?? 0) <= 0) continue;
      const sum = role.planning_pct + role.execution_pct + role.testing_gl_pct;
      if (Math.abs(sum - 1.0) > 0.02) {
        toast.error(`${ROLE_LABEL_MAP[role.role_key] ?? role.role_key} splits sum to ${Math.round(sum * 100)}% — must be 100%.`);
        return;
      }
    }
    mutation.mutate(draft, {
      onSuccess: () => toast.success("Role config saved — capacity views refreshing."),
      onError: (err) => toast.error(`Save failed: ${(err as Error).message}`),
    });
  };

  const isDirty = draft && config?.roles && JSON.stringify(draft) !== JSON.stringify(config.roles);

  if (isLoading) {
    return <div className="text-xs text-ink-subtle py-2">Loading role config...</div>;
  }

  return (
    <div className="rounded-lg border border-line bg-canvas/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Role Configuration
        </div>
        <button
          onClick={handleSave}
          disabled={!isDirty || mutation.isPending}
          className={cn(
            "rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors",
            isDirty
              ? "bg-brand-navy-600 text-white hover:bg-brand-navy-700"
              : "bg-muted text-ink-subtle cursor-not-allowed",
          )}
        >
          {mutation.isPending ? "Saving..." : "Save Config"}
        </button>
      </div>
      <div className="text-[10px] text-ink-subtle mb-2">
        Pick the roles this project needs. Allocation = % of est. hours. Phase splits must sum to 100%.
      </div>
      {activeRoles.length === 0 ? (
        <div className="rounded-md border border-dashed border-line-strong bg-surface px-3 py-4 text-center text-[11px] text-ink-muted">
          No roles selected yet. Use “Add a role” below to pick the ones this project needs.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-line">
                <th className="pb-1.5 pr-2 text-left font-semibold text-ink-muted">Role</th>
                <th className="pb-1.5 pr-2 text-right font-semibold text-ink-muted">Alloc%</th>
                <th className="pb-1.5 pr-2 text-right font-semibold text-sky-600 dark:text-sky-400">Plan</th>
                <th className="pb-1.5 pr-2 text-right font-semibold text-amber-600 dark:text-amber-400">Exec</th>
                <th className="pb-1.5 pr-2 text-right font-semibold text-emerald-600 dark:text-emerald-400">Test/GL</th>
                <th className="pb-1.5 pr-2 text-right font-semibold text-ink-subtle">Sum</th>
                <th className="pb-1.5 w-6" aria-label="Remove role" />
              </tr>
            </thead>
            <tbody>
              {activeRoles.map((role) => {
                const sum = role.planning_pct + role.execution_pct + role.testing_gl_pct;
                const sumOk = Math.abs(sum - 1.0) <= 0.02;
                return (
                  <tr key={role.role_key} className="border-b border-line-soft last:border-b-0">
                    <td className="py-1.5 pr-2 font-medium text-ink-2">
                      {ROLE_LABEL_MAP[role.role_key] ?? role.role_key}
                      {role.is_override && (
                        <span className="ml-1 text-[8px] text-brand-navy-500 font-semibold">CUSTOM</span>
                      )}
                    </td>
                    <td className="py-1 pr-2 text-right">
                      <MiniPctInput value={role.allocation_pct} onChange={(v) => handleChange(role.role_key, "allocation_pct", v)} />
                    </td>
                    <td className="py-1 pr-2 text-right">
                      <MiniPctInput value={role.planning_pct} onChange={(v) => handleChange(role.role_key, "planning_pct", v)} />
                    </td>
                    <td className="py-1 pr-2 text-right">
                      <MiniPctInput value={role.execution_pct} onChange={(v) => handleChange(role.role_key, "execution_pct", v)} />
                    </td>
                    <td className="py-1 pr-2 text-right">
                      <MiniPctInput value={role.testing_gl_pct} onChange={(v) => handleChange(role.role_key, "testing_gl_pct", v)} />
                    </td>
                    <td className="py-1.5 pr-2 text-right">
                      <span className={cn(
                        "inline-flex min-w-[32px] justify-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
                        sumOk ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
                      )}>
                        {Math.round(sum * 100)}%
                      </span>
                    </td>
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => removeRole(role.role_key)}
                        title="Remove this role from the project"
                        className="rounded p-1 text-ink-faint hover:bg-rose-50 dark:hover:bg-rose-500/15 hover:text-rose-500 transition-colors"
                        aria-label="Remove role"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Add a role picker — only shows roles not already selected. */}
      {unusedRoles.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] text-ink-subtle">Add a role:</span>
          {unusedRoles.map((r) => (
            <button
              key={r.role_key}
              type="button"
              onClick={() => addRole(r.role_key)}
              className="rounded-md border border-line bg-surface px-2 py-0.5 text-[10px] font-medium text-ink-3 hover:border-brand-navy-300 hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-700 dark:hover:text-brand-navy-300 transition-colors"
            >
              + {ROLE_LABEL_MAP[r.role_key] ?? r.role_key}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniPctInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const display = Math.round(value * 100);
  return (
    <input
      type="number"
      min={0}
      max={100}
      step={5}
      value={display}
      onChange={(e) => {
        const parsed = parseFloat(e.target.value);
        if (!isNaN(parsed)) onChange(Math.max(0, Math.min(1, parsed / 100)));
      }}
      className="w-14 rounded border border-line bg-surface px-1.5 py-1 text-center text-[11px] tabular-nums focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
    />
  );
}

const inputCls =
  "w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-100";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
